/**
 * Register funnel analytics - rastreia eventos do cadastro para identificar bugs e drop-off.
 * Envia eventos para o backend (fire-and-forget) para análise posterior.
 */

import { Platform } from 'react-native';
import * as Sentry from '@sentry/react-native';
import api from '@/services/api';
import { getAttributionFromCookie } from '@/lib/attribution';

export type RegisterAnalyticsEvent =
  | 'register_page_view'
  | 'register_step_viewed'
  | 'register_step_completed'
  | 'register_validation_error'
  | 'register_api_error'
  | 'register_success'
  | 'register_google_click'
  | 'register_google_error'
  | 'register_apple_click'
  | 'register_apple_error'
  | 'register_check_email_error'
  | 'register_check_email_start'
  | 'register_check_email_ok'
  | 'register_button_click'
  | 'register_external_prompt_view'
  | 'register_external_open';

export interface RegisterAnalyticsPayload {
  event: RegisterAnalyticsEvent;
  step?: number;
  role?: string;
  /** Tipo do botão: next | back | submit */
  buttonType?: 'next' | 'back' | 'submit';
  /** Campo que falhou na validação */
  errorField?: string;
  /** Mensagem de erro (sem dados sensíveis) */
  errorMessage?: string;
  /** Status HTTP em erros de API */
  httpStatus?: number;
  /** ID de sessão (para agrupar eventos do mesmo usuário) */
  sessionId?: string;
  /** UTM/attribution para correlacionar com tráfego pago */
  utmSource?: string;
  utmCampaign?: string;
  /** Se estava completando perfil (Google OAuth) */
  isCompletingProfile?: boolean;
}

const SESSION_ID_KEY = 'register_analytics_session';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sid = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

function getAttributionContext(): Partial<RegisterAnalyticsPayload> {
  if (typeof window === 'undefined') return {};
  const att = getAttributionFromCookie();
  if (!att) return {};
  return {
    utmSource: att.utmSource ?? undefined,
    utmCampaign: att.utmCampaign ?? undefined,
  };
}

/**
 * Envia evento de analytics (fire-and-forget). Não bloqueia a UI.
 */
export function trackRegisterEvent(payload: RegisterAnalyticsPayload): void {
  Sentry.addBreadcrumb({
    category: 'register',
    message: payload.event,
    level: payload.event.endsWith('_error') ? 'error' : 'info',
    data: { step: payload.step, errorField: payload.errorField, httpStatus: payload.httpStatus },
  });

  // Não envia em development
  if (__DEV__) return;
  // Só envia no web (onde geralmente roda o tráfego pago)
  if (Platform.OS !== 'web') return;

  const sessionId = getOrCreateSessionId();
  const attribution = getAttributionContext();

  const body = {
    ...payload,
    sessionId,
    ...attribution,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };

  api.post('/analytics/register-events', body).catch(() => {
    // Silencioso - não queremos que falha de analytics quebre o fluxo
  });
}
